import { FC } from 'react'

import { Box, RadioButton, Text } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  ContractsAnnualWorkingTimeDistributionEnum,
  ContractsBankHolidayTreatmentEnum,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import DropdownSearchBar from 'modules/core/components/DropdownSearchBar'
import DaysSelector from 'modules/core/components/Inputs/DaysSelector'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import TextInput from 'modules/core/components/Inputs/Text'
import Loading from 'modules/core/components/Loading'
import NumberField from 'modules/core/components/NumberField'
import { useEffect } from 'modules/core/hooks/useEffect'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { translations as workingHoursTranslations } from 'modules/contracts/components/FixFlow/lib/workingHoursTranslations'
import { ContractFlowType } from 'modules/contracts/components/FlowPage'
import FormWrapper from 'modules/contracts/components/FlowPage/FormWrapper'
// TODO: Move ContractTypeSelect to the Work Hours folder when dev_time_tracking_workable_days_definition is removed
import ContractTypeSelect from 'modules/contracts/components/FlowPage/Steps/LegalAndExtras/_ContractTypeSelect'
import useCanSeeYearlyBalance from 'modules/contracts/hooks/useCanSeeYearlyBalance'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { useLegalEntityContractData } from 'modules/contracts/lib/legalEntityContractDataContext'
import { translate } from 'modules/contracts/lib/translate'
import ContractVersion from 'modules/contracts/store/models/ContractVersion'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import MaximumHoursHint from './MaximumHoursHint'
import WorkHoursHint from './WorkHoursHint'
import {
  MAX_PERCENTAGE_WORKING_HOURS,
  MIN_PERCENTAGE_WORKING_HOURS,
  constraintToWorkingHoursTimePercentage,
  stepHasErrors,
} from './validator'

const CENTS = 100

interface Props {
  form: Form
  withFormWrapper?: boolean
  translations?: { [key: string]: string }
  step?: number
  type?: ContractFlowType
  setIsCurrentStepCompleted?: (isCompleted: boolean) => void
  legalEntity: LegalEntity
}

export const WorkHours = observer(
  ({
    form,
    step,
    type = 'fix',
    legalEntity,
    withFormWrapper,
    setIsCurrentStepCompleted,
    translations = workingHoursTranslations(),
  }: Props) => {
    const { loading, canSeeYearlyBalance } = useCanSeeYearlyBalance()
    const { spanishContractData } = useLegalEntityContractData()
    const frequency = ContractVersion.WorkingHoursFrequency()
    const { employee, contractVersion } = useContractFlow()
    const canSeeWorkingHours: boolean = policyAuthorizations.for(
      'contracts.see_working_hours',
      employee
    )
    const canSeeContractType: boolean = policyAuthorizations.for(
      'contracts.see_contract_type',
      employee
    )
    const workingHoursField = canSeeWorkingHours ? form.get('working_hours') : undefined
    const workingHoursFrequencyField = canSeeWorkingHours
      ? form.get('working_hours_frequency')
      : undefined
    const workingHoursValue = workingHoursField ? workingHoursField.value : ''
    const workingHoursFrequencyValue = workingHoursFrequencyField
      ? workingHoursFrequencyField.value
      : ''
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
      if (workingHoursField) workingHoursField.setErrors(null)
      if (workingHoursFrequencyField) workingHoursFrequencyField.setErrors(null)
      setIsCurrentStepCompleted?.(!stepHasErrors(form))
    }, [workingHoursValue, workingHoursFrequencyValue])
    /* eslint-enable react-hooks/exhaustive-deps */

    const frequencyOptions: SelectOption[] = Object.keys(frequency).map((key) => ({
      label: frequency[key] ?? '',
      value: key,
    }))

    const workingDayTypes = spanishContractData.workingDayTypes
    const workingDayTypeField = form.get('es_working_day_type_id')

    const workingDayTypesOptions = workingDayTypes
      .filter((workingDayType) => !workingDayType.archivedAt)
      .map((x) => {
        return {
          label: x.name,
          value: x.id,
        }
      })

    const workingType = () => {
      const id = form.get('es_working_day_type_id').value
      const option = workingDayTypesOptions.find((e) => String(e.value) === String(id))
      return option ? option.label : `-- ${i18n.t('common.none')} --`
    }

    const workingHoursName = () => {
      const currentOption = frequencyOptions.find(
        (e) => e.value === form.get('working_hours_frequency').value
      )
      return currentOption?.label || `-- ${i18n.t('common.none')} --`
    }

    if (!canSeeWorkingHours) return <></>
    if (loading) return <Loading />

    const isContractHoursSet = form.get('working_hours').value
    const isWorkingFrequencySet = form.get('working_hours_frequency').value
    const legalEntityCountry = legalEntity.attributes?.country || legalEntity.country

    const bankHolidayTreatmentOptions: {
      label: string
      value: ContractsBankHolidayTreatmentEnum
    }[] = [
      {
        label: translate(translations, 'non_workable') || '',
        value: 'non_workable',
      },
      {
        label: translate(translations, 'workable') || '',
        value: 'workable',
      },
    ]

    const annualWorkingTimeDistributionOptions: {
      value: ContractsAnnualWorkingTimeDistributionEnum
      label: string
      description: string
    }[] = [
      {
        value: 'limit_workdays',
        label: i18n.t(
          'add_employee_modal.contract_information_step.fields.annual_working_time_distribution.limit_workdays'
        ),
        description: i18n.t(
          'add_employee_modal.contract_information_step.fields.annual_working_time_distribution.limit_workdays_description'
        ),
      },
      {
        value: 'limit_daily_hours',
        label: i18n.t(
          'add_employee_modal.contract_information_step.fields.annual_working_time_distribution.limit_daily_hours'
        ),
        description: i18n.t(
          'add_employee_modal.contract_information_step.fields.annual_working_time_distribution.limit_daily_hours_description'
        ),
      },
    ]

    const isSpanish =
      contractVersion && type === 'fix' ? contractVersion.isSpanish : legalEntity.country === 'es'
    const isFrench =
      contractVersion && type === 'fix' ? contractVersion.isFrench : legalEntity.country === 'fr'
    const isGerman =
      contractVersion && type === 'fix' ? contractVersion.isGerman : legalEntity.country === 'de'
    const isPortuguese =
      contractVersion && type === 'fix'
        ? contractVersion.isPortuguese
        : legalEntity.country === 'pt'
    const hasContractType = isSpanish || isFrench || isGerman || isPortuguese

    const hasAnnualisationDailyValue = hasFeature('dev_time_tracking_annualisation_daily_value')
    const maxLegalYearlyHoursValue = form.get('max_legal_yearly_hours')?.value
    const showAnnualWorkingTimeDistribution =
      hasAnnualisationDailyValue && !!maxLegalYearlyHoursValue

    return hasFeature('dev_time_tracking_workable_days_definition') ? (
      <Box gap="s24">
        <FormWrapper
          withFormWrapper={withFormWrapper}
          hint={
            step && (
              <WorkHoursHint
                step={step}
                type={type}
                isContractHoursSet={isContractHoursSet}
                isWorkingFrequencySet={isWorkingFrequencySet}
              />
            )
          }
        >
          <Box gap="s16">
            <Box gap="s16">
              <Box flexDirection="row" gap="s16" width="full">
                <Box width="full">
                  <TextInput
                    field={form.get('working_hours')}
                    type="number"
                    min={0}
                    label={translate(translations, 'hours')}
                    placeholder={translate(translations, 'enter')}
                  />
                </Box>
                <Box width="full">
                  <DropdownSearchBar
                    name={workingHoursName()}
                    label={translate(translations, 'period') || ''}
                    onSelect={(item) => {
                      form.get('working_hours_frequency').set(item.value)
                    }}
                    labelNotFound={i18n.t(
                      'contract_versions.forms.basic_information.option_not_found'
                    )}
                    data={frequencyOptions}
                    searchLabel={i18n.t(
                      'contract_versions.forms.contract_extra_information.a3innuva.search_label'
                    )}
                    popoverWidth="s300"
                  />
                </Box>
              </Box>
              <Box gap="s8">
                <Text.Normal color="grey1000" weight="medium" size="200">
                  {translate(translations, 'days')}
                </Text.Normal>
                <Box paddingLeft="s24" alignItems="flexStart">
                  <DaysSelector field={form.get('working_week_days')} canUnselectAll />
                </Box>
              </Box>

              {canSeeYearlyBalance && (
                <Box gap="s16">
                  <Box>
                    <TextInput
                      field={form.get('max_legal_yearly_hours')}
                      type="number"
                      min={0}
                      label={translate(translations, 'max_annual_hours')}
                    />
                  </Box>
                  <Box>
                    <TextInput
                      field={form.get('maximum_weekly_hours')}
                      type="number"
                      min={0}
                      label={translate(translations, 'max_weekly_hours')}
                    />
                  </Box>
                  <Box>
                    <TextInput
                      field={form.get('max_work_minutes_per_day')}
                      type="number"
                      min={0}
                      label={translate(translations, 'max_work_minutes_per_day')}
                    />
                  </Box>
                  <Box>
                    <TextInput
                      field={form.get('min_rest_minutes_between_days')}
                      type="number"
                      min={0}
                      label={translate(translations, 'min_rest_minutes_between_days')}
                    />
                  </Box>
                </Box>
              )}
            </Box>
            <Box gap="s32" flexDirection="column">
              <Box gap="s16">
                <Box gap="s8">
                  <Box gap="s2">
                    <Text.Normal color="grey1000" weight="medium" size="200">
                      {translate(translations, 'bank_holidays_treatment')}
                    </Text.Normal>
                  </Box>
                  <Box gap="s8">
                    {bankHolidayTreatmentOptions.map((option) => (
                      <RadioButton
                        key={option.value}
                        label={option.label}
                        size="small"
                        onChange={() => form.get('bank_holiday_treatment').set(option.value)}
                        value={option.value}
                        checked={form.get('bank_holiday_treatment').value === option.value}
                      />
                    ))}
                  </Box>
                </Box>
                {showAnnualWorkingTimeDistribution && (
                  <Box gap="s32" flexDirection="column">
                    <Box gap="s8">
                      <Box gap="s2">
                        <Text.Normal color="grey1000" weight="medium" size="200">
                          {translate(translations, 'annual_working_time_distribution')}
                        </Text.Normal>
                      </Box>
                      <Box gap="s8">
                        {annualWorkingTimeDistributionOptions.map((option) => (
                          <Box key={option.value} flexDirection="row" alignItems="center" gap="s8">
                            <RadioButton
                              key={option.value}
                              label={option.label}
                              size="small"
                              onChange={() =>
                                form.get('annual_working_time_distribution').set(option.value)
                              }
                              value={option.value}
                              checked={
                                form.get('annual_working_time_distribution').value === option.value
                              }
                            />
                            <InformationTooltip information={option.description} />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
              <Box gap="s16">
                <Box>
                  <Text size="400" weight="medium" color="grey1000">
                    {translate(translations, 'employment_type')}
                  </Text>
                </Box>
                {canSeeContractType && hasContractType && (
                  <ContractTypeSelect form={form} type={type} />
                )}
                {legalEntityCountry === 'es' && (
                  <>
                    <DropdownSearchBar
                      name={workingType()}
                      label={i18n.t(
                        'add_employee_modal.contract_information_step.fields.working_day_type.label'
                      )}
                      onSelect={(item) => {
                        workingDayTypeField?.set(item.value)
                      }}
                      labelNotFound={i18n.t(
                        'contract_versions.forms.basic_information.work_day_type_not_found'
                      )}
                      data={workingDayTypesOptions}
                      searchLabel={i18n.t(
                        'contract_versions.forms.contract_extra_information.a3innuva.search_label'
                      )}
                      loading={loading}
                      popoverWidth="s400"
                    />
                  </>
                )}
                <Box>
                  <NumberField
                    label={translate(translations, 'working_time_percentage_in_cents')}
                    placeholder={translate(translations, 'working_time_percentage_placeholder')}
                    value={
                      form.get('working_time_percentage_in_cents').value
                        ? form.get('working_time_percentage_in_cents').value / CENTS
                        : undefined
                    }
                    onChange={(value: number | null) =>
                      form
                        .get('working_time_percentage_in_cents')
                        .set(
                          value !== null
                            ? constraintToWorkingHoursTimePercentage(value) * CENTS
                            : null
                        )
                    }
                    step={1}
                    min={MIN_PERCENTAGE_WORKING_HOURS}
                    max={MAX_PERCENTAGE_WORKING_HOURS}
                    indicator="%"
                    maxDecimals={0}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </FormWrapper>
      </Box>
    ) : (
      <Box gap="s24">
        <FormWrapper
          withFormWrapper={withFormWrapper}
          hint={
            step && (
              <WorkHoursHint
                step={step}
                type={type}
                isContractHoursSet={isContractHoursSet}
                isWorkingFrequencySet={isWorkingFrequencySet}
              />
            )
          }
        >
          <Box flexDirection="row" gap="s16">
            <TextInput
              field={form.get('working_hours')}
              type="number"
              min={0}
              label={translate(translations, 'hours')}
            />
            <DropdownSearchBar
              name={workingHoursName()}
              label={translate(translations, 'frequency') || ''}
              onSelect={(item) => {
                form.get('working_hours_frequency').set(item.value)
              }}
              labelNotFound={i18n.t('contract_versions.forms.basic_information.option_not_found')}
              data={frequencyOptions}
              searchLabel={i18n.t(
                'contract_versions.forms.contract_extra_information.a3innuva.search_label'
              )}
              popoverWidth="s300"
            />
          </Box>
        </FormWrapper>
        {legalEntityCountry === 'es' && (
          <FormWrapper withFormWrapper={withFormWrapper}>
            <DropdownSearchBar
              name={workingType()}
              label={i18n.t(
                'add_employee_modal.contract_information_step.fields.working_day_type.label'
              )}
              onSelect={(item) => {
                workingDayTypeField?.set(item.value)
              }}
              labelNotFound={i18n.t(
                'contract_versions.forms.basic_information.work_day_type_not_found'
              )}
              data={workingDayTypesOptions}
              searchLabel={i18n.t(
                'contract_versions.forms.contract_extra_information.a3innuva.search_label'
              )}
              loading={loading}
              popoverWidth="s400"
            />
          </FormWrapper>
        )}
        <FormWrapper withFormWrapper={withFormWrapper}>
          <Box gap="s8">
            <Text.Normal color="grey1000" weight="normal" size="200">
              {translate(translations, 'days')}
            </Text.Normal>
            <Box paddingLeft="s24" alignItems="flexStart">
              <DaysSelector field={form.get('working_week_days')} canUnselectAll />
            </Box>
          </Box>
        </FormWrapper>
        {canSeeYearlyBalance && (
          <>
            <FormWrapper withFormWrapper={withFormWrapper} hint={step && <MaximumHoursHint />}>
              <Box flexDirection="row" gap="s16">
                <TextInput
                  field={form.get('max_legal_yearly_hours')}
                  type="number"
                  min={0}
                  label={translate(translations, 'max_legal_yearly_hours')}
                />
              </Box>
            </FormWrapper>
            <FormWrapper withFormWrapper={withFormWrapper}>
              <Box flexDirection="row" gap="s16">
                <TextInput
                  field={form.get('maximum_weekly_hours')}
                  type="number"
                  min={0}
                  label={translate(translations, 'maximum_weekly_hours')}
                />
              </Box>
            </FormWrapper>
          </>
        )}
      </Box>
    )
  }
)

const WorkHoursWrapper: FC<Props> = (props) => {
  const { translations } = props
  return (
    <Box gap="s32">
      <FormWrapper>
        <Text size="400" weight="medium" color="grey1000">
          {translate(translations || workingHoursTranslations(), 'header')}
        </Text>
      </FormWrapper>
      <WorkHours {...props} />
    </Box>
  )
}

export default observer(WorkHoursWrapper)
